import type { TodoCard } from '@cortex/domain-ac-mens-hub-api';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatShortDate, jstToday } from '../../lib/issue-mgmt-format.js';
import { cardQuestion, TODO_TYPE_LABEL_KEYS } from '../../lib/issue-mgmt-next-action.js';
import type { TodoRow } from '../../lib/issue-mgmt-todo.js';

/**
 * カードの期限の表示（済み・期限切れは赤・今日まで・◯◯まで・期限の無いカードは出た日から）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることのカードの期限を、済み・期限切れ（赤）・今日まで・日付まで・出た日からで表示する
 * @graph-connects formatShortDate [calls] 日付
 * @graph-connects jstToday [calls] 今日
 */
export function TodoDue({ card, done, now }: { card: TodoCard; done: boolean; now: Date }) {
  const { t } = useTranslation();
  if (done)
    return <span className="text-[var(--color-text-muted)]">{t('issueMgmt.todo.done')}</span>;
  if (card.dueDate === null) {
    return (
      <span className="text-[var(--color-text-muted)]">
        {card.since === null
          ? ''
          : t('issueMgmt.todo.since', { date: formatShortDate(card.since, now) })}
      </span>
    );
  }
  const date = formatShortDate(card.dueDate, now);
  if (card.overdue) {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-[var(--color-error)]">
        <Clock className="h-3 w-3" aria-hidden="true" />
        {t('issueMgmt.todo.overdue', { date })}
      </span>
    );
  }
  return (
    <span className="text-[var(--color-text-muted)]">
      {card.dueDate === jstToday(now)
        ? t('issueMgmt.todo.dueToday')
        : t('issueMgmt.todo.dueOn', { date })}
    </span>
  );
}

/**
 * やることの左の一覧。見出しに「N件中M件目／残り K件」、行はカードの種類・期限・問い（タイトル）1行。
 * 開いているカードは左に線、済みは取り消し線と結果を1行。行を押すとそのカードを開く。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることのカードを種類・期限・タイトルで縦に並べ、何件中何件目と残りの件数を出し、押したカードを開く
 * @graph-connects TodoDue [calls] 期限の表示
 * @graph-connects cardQuestion [calls] カードの問い
 */
export function TodoCardList({
  rows,
  selectedId,
  leftCount,
  onSelect,
  now,
}: {
  rows: readonly TodoRow[];
  selectedId: string | null;
  leftCount: number;
  onSelect: (cardId: string) => void;
  now: Date;
}) {
  const { t } = useTranslation();
  const position = rows.findIndex((row) => row.card.cardId === selectedId) + 1;
  return (
    <nav
      aria-label={t('issueMgmt.todo.listLabel')}
      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]"
    >
      <div className="flex justify-between border-b border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-text-muted)]">
        <span>
          {t('issueMgmt.todo.position', { total: String(rows.length), n: String(position) })}
        </span>
        <span>{t('issueMgmt.todo.left', { n: String(leftCount) })}</span>
      </div>
      <ul className="max-h-[32rem] overflow-y-auto">
        {rows.map(({ card, done }) => {
          const selected = card.cardId === selectedId;
          return (
            <li key={card.cardId}>
              <button
                type="button"
                aria-current={selected ? 'true' : undefined}
                onClick={() => {
                  onSelect(card.cardId);
                }}
                className={`block w-full border-b border-l-4 border-[var(--color-border)] px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--color-bg-hover)] ${
                  selected
                    ? 'border-l-[var(--color-accent-primary)] bg-[var(--color-bg-elevated)]'
                    : 'border-l-transparent'
                }`}
              >
                <span className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-semibold text-[var(--color-text-secondary)]">
                    {t(TODO_TYPE_LABEL_KEYS[card.type])}
                  </span>
                  <TodoDue card={card} done={done !== null} now={now} />
                </span>
                <span
                  className={`mt-0.5 block truncate ${
                    done === null
                      ? 'text-[var(--color-text-primary)]'
                      : 'text-[var(--color-text-muted)] line-through'
                  }`}
                >
                  {cardQuestion(card, t)}
                </span>
                {done !== null && (
                  <span className="mt-0.5 block truncate text-xs text-[var(--color-text-muted)]">
                    {done.label}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
